import React from 'react'

import { useParams } from 'react-router-dom'

import { Box, CircularProgress } from '@material-ui/core'

import styles from './styles'
import { Container, ContentHeader } from 'components'
import * as service from 'service'
import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'
import ContractsEditForm from './components/ContractsEditForm'
import ContractsEditTaxes from './components/ContractsEditTaxes'
import ContractsEditPlans from './components/ContractsEditPlans'
import CollapseProvider from 'providers/CollapseProvider'
import constants from '../../constants'
import { useFetch } from '@yapay/react-components/hooks'
import { makeStyles } from '@material-ui/styles'

const useStyles = makeStyles(styles)

const ContractsEdit = () => {
  const classes = useStyles()
  const params = useParams()
  const snackbar = useSnackbar()
  const [contract, setContract] = React.useState(null)

  const loadContract = React.useCallback(async () => {
    const [responseContract, responseContractEmails] = await Promise.all([
      service.recurrence.contracts.get({ contractId: params.contractId }),
      service.recurrence.contractEmails.get({ contractId: params.contractId }),
    ])

    setContract({
      ...responseContract.data.contract,
      contractPlans: helpers.contractPlans.filterVisible(
        responseContract.data.contract.contractPlans,
      ),
      contractEmails: responseContractEmails.data.contractEmails,
    })
  }, [params])

  const {
    response: responseGenerateNfeOnInvoiceCreation,
    isLoading: isLoadingGenerateNfeOnInvoiceCreation,
  } = useFetch(service.recurrence.settings.get, {
    settingId: constants.settings.GENERATE_NFE_ON_INVOICE_CREATION,
  })

  const {
    response: responseInvoiceInterval,
    isLoading: isLoadingInvoiceInterval,
  } = useFetch(service.recurrence.settings.get, {
    settingId: constants.settings.INVOICE_INTERVAL,
  })

  const {
    response: responseDaysToGenerateNFe,
    isLoading: isLoadingDaysToGenerateNFe,
  } = useFetch(service.recurrence.settings.get, {
    settingId: constants.settings.DAYS_TO_GENERATE_NFE,
  })

  const generalSettings = {
    generateNfeOnInvoiceCreation: Number(
      responseGenerateNfeOnInvoiceCreation?.data?.setting?.value,
    ),
    invoiceInterval: responseInvoiceInterval?.data?.setting?.value,
    daysToGenerateNfe: responseDaysToGenerateNFe?.data?.setting?.value,
  }

  React.useEffect(() => {
    loadContract()
  }, [loadContract])

  const handleSubmitContractEdit = async data => {
    try {
      const deletedContractEmails = contract.contractEmails.filter(
        contractEmail => !data.contractEmails.includes(contractEmail.email),
      )

      const newEmails = data.contractEmails.filter(
        email =>
          !contract.contractEmails.some(
            contractEmail => contractEmail.email === email,
          ),
      )

      await Promise.all([
        service.recurrence.contracts.update({
          contractId: params.contractId,
          contract: {
            dayExpiration: data.dayExpiration,
            urlNotification: data.urlNotification,
            settings: [
              {
                settingTag: 'days_to_overdue_invoice',
                value: data.settings.daysToOverdueInvoice,
              },
              {
                settingTag: 'invoice_interval',
                value: data.settings.invoiceInterval,
              },
              {
                settingTag: 'days_to_generate_nfe',
                value: data.settings.daysToGenerateNfe,
              },
              {
                settingTag: 'generate_service_tax_invoices',
                value: data.settings.generateServiceTaxInvoices,
              },
              {
                settingTag: 'fixed_tax_invoice_description',
                value: data.settings.fixedTaxInvoiceDescription,
              },
            ],
          },
        }),
      ])

      await Promise.all(
        deletedContractEmails.map(contractEmail =>
          service.recurrence.contractEmails.destroy({
            contractId: contract.id,
            contractEmailId: contractEmail.id,
          }),
        ),
      )

      await Promise.all(
        newEmails.map(email =>
          service.recurrence.contractEmails.create({
            contractId: contract.id,
            name: email,
            email,
          }),
        ),
      )

      await loadContract()

      snackbar.open({
        message: 'Assinatura alterada com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: 'Erro ao alterar assinatura!',
        variant: 'error',
      })
    }
  }

  return (
    <Container>
      <ContentHeader title="Assinatura" subtitle="Editar" />

      <div className={classes.root}>
        {!isLoadingInvoiceInterval &&
        !isLoadingDaysToGenerateNFe &&
        !isLoadingGenerateNfeOnInvoiceCreation &&
        contract ? (
          <>
            <Box mb={4}>
              <ContractsEditForm
                contract={contract}
                generalSettings={generalSettings}
                onSubmit={handleSubmitContractEdit}
              />
            </Box>

            <Box mb={4}>
              <ContractsEditTaxes contract={contract} />
            </Box>

            <Box>
              <CollapseProvider>
                <ContractsEditPlans contract={contract} />
              </CollapseProvider>
            </Box>
          </>
        ) : (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        )}
      </div>
    </Container>
  )
}

export default ContractsEdit
